<template>
  <div
    ref="inputWrapper"
    class="px-2 py-4.5 w-full flex items-center rounded-lg cursor-text relative border border-transparent z-40 transition-all duration-200 min-h-14 inline-input"
    :class="{'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
    @click="!focused ? focus() : ''"
  >
    <span
      ref="label"
      class="top-4.5 text-16 font-normal absolute z-20 leading-5 text-gray transition-all duration-200 pointer-events-none"
      :class="{[labelClass]: true, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    >
      {{ label }}
    </span>

    <editor
      ref="input"
      style="transform: translate(0)"
      :key="editorKey"
      :config="config"
      class="input w-full mt-5 leading-5 text-black duration-200 opacity-0"
      :class="{'text-gray': disabled, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
      :style="disabled ? {pointerEvents: 'none'} : {}"
    />
    <div ref="rightSide" class="right-2 bottom-4 absolute cursor-pointer">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import helpers from '@/utils/helpers'
  import {store} from '@/integrations/store'

  // EDITOR
  import Header from '@editorjs/header'
  import List from '@editorjs/list'
  import Link from '@editorjs/link'
  // import CodeTool from '@editorjs/code'
  import Paragraph from '@editorjs/paragraph'
  import Embed from '@editorjs/embed'
  import Table from '@editorjs/table'
  // import Checklist from '@editorjs/checklist'
  import Marker from '@editorjs/marker'
  import Warning from '@editorjs/warning'
  // import RawTool from '@editorjs/raw'
  import Quote from '@editorjs/quote'
  import InlineCode from '@editorjs/inline-code'
  import Delimiter from '@editorjs/delimiter'
  // import Image from '@editorjs/image'

  const API_URL = document.querySelector('meta[name="app-url"]').content
  const token_access = helpers.isPreviewFrame() ? store.getters['user/userTokenAdmin'] : store.getters['user/userTokenAccess']
  export default {
    name: 'edjs-input',
    props: {
      label: {
        type: String,
        required: true
      },
      labelClass: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      value: Object,
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        inputVal: {
          blocks: []
        },
        interval: null,
        fieldError: '',
        errHeight: 0,
        focused: false,
        isBluring: false,
        isValid: true,
        quillHeight: 0,
        editorKey: 0.01,
        config: {
          tools: {
            header: {
              class: Header,
              config: {
                placeholder: 'Enter a header',
                levels: [2, 3, 4],
                defaultLevel: 3
              }
            },
            list: {
              class: List,
              inlineToolbar: true
            },
            // checklist: {
            //   class: Checklist
            // },
            // image: {
            //   class: Image,
            //   config: {
            //     uploader: {
            //       uploadByFile: file => {
            //         return new Promise((resolve, reject) => {
            //           const reader = new FileReader()
            //           reader.readAsDataURL(file)

            //           reader.onload = () => {
            //             if (reader.result.length < 512000) {
            //               resolve({
            //                 success: 1,
            //                 file: {url: reader.result}
            //               })
            //             } else {
            //               reject('Image too big')
            //             }
            //           }
            //           reader.onerror = error => reject(error)
            //         })
            //       }
            //     }
            //   }
            // },
            linkTool: {
              class: Link,
              config: {
                endpoint: API_URL + '/pwa/get-url-preview',
                headers: {
                  'access-token': token_access
                }
              }
            },
            quote: {
              class: Quote,
              inlineToolbar: true,
              shortcut: 'CMD+SHIFT+O',
              config: {
                quotePlaceholder: 'Enter a quote',
                captionPlaceholder: "Quote's author"
              }
            },
            delimiter: Delimiter,
            table: {
              class: Table,
              inlineToolbar: true,
              config: {
                rows: 2,
                cols: 3
              }
            },
            warning: {
              class: Warning,
              inlineToolbar: true,
              shortcut: 'CMD+SHIFT+W',
              config: {
                titlePlaceholder: 'Title',
                messagePlaceholder: 'Message'
              }
            },
            // code: {
            //   class: CodeTool
            // },
            paragraph: {
              class: Paragraph
            },
            embed: {
              class: Embed,
              config: {
                services: {
                  youtube: true,
                  coub: true,
                  imgur: true
                }
              }
            },
            Marker: {
              class: Marker,
              shortcut: 'CMD+SHIFT+M'
            },
            // raw: RawTool,
            inlineCode: {
              class: InlineCode,
              shortcut: 'CMD+SHIFT+M'
            }
          },
          onChange: async () => {
            this.data = await this.$refs.input.state.editor.save()
            this.inputVal = this.data
            this.input()
          },
          onReady: async () => {
            const editorHolder = this.$refs.input.$el

            editorHolder.addEventListener('focusin', () => {
              this.focus()
            })
            editorHolder.addEventListener('focusout', () => {
              setTimeout(() => {
                this.blur()
              }, 200)
            })
            const toolbar = document.querySelector('.ce-toolbar__actions')
            const popover = document.querySelector('.ce-popover')
            if (toolbar) {
              toolbar.addEventListener('focusin', () => {
                this.menuOpened = true
              })
              toolbar.addEventListener('focusout', () => {
                setTimeout(() => {
                  if (this.menuClick === false) {
                    this.menuOpened = false
                    this.focus()
                  }
                }, 200)
              })
            }
            if (popover) {
              popover.addEventListener('click', () => {
                this.menuClick = true
                setTimeout(() => {
                  this.menuClick = false
                }, 250)
              })
            }
          },
          placeholder: ''
        },
        menuOpened: false,
        menuClick: false,
        data: {
          blocks: []
        }
      }
    },
    async mounted() {
      await this.componentLoaded()
      this.$nextTick(() => {
        this.data.blocks = this.value.blocks || []
        this.quillHeight = this.$refs.input.$el.clientHeight
        this.$refs.input.$el.classList.add('hidden')
        if (this.value !== null && this.value !== '') this.init()
      })
    },
    methods: {
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input.$el && this.$refs.inputWrapper && this.$refs.label && this.$refs.rightSide && this.$refs.error) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      input() {
        this.$emit('input', this.getValue())
      },
      init() {
        if (!this.focused && this.label) {
          this.focused = true
          this.config.data = this.value
          this.editorKey++
          this.$refs.input.$el.style.height = '0px'
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          this.$refs.input.$el.classList.replace('hidden', 'flex')
          setTimeout(() => {
            this.$refs.input.$el.classList.replace('opacity-0', 'opacity-100')
            this.$refs.input.$el.style.height = this.quillHeight + 'px'
            setTimeout(() => {
              this.$refs.input.$el.style.height = ''
            }, 200)
          }, 0)
        }

        this.$emit('focus')
      },
      focus() {
        if (!this.focused && this.label && !this.isBluring) {
          this.focused = true
          this.$refs.input.$el.style.height = '0px'
          this.$refs.inputWrapper.classList.replace(['px-2', 'py-4.5'], ['p-2'])
          this.$refs.label.classList.replace(
            ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal'],
            ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold']
          )
          this.$refs.input.$el.classList.replace('hidden', 'flex')
          setTimeout(() => {
            this.$refs.input.$el.classList.replace('opacity-0', 'opacity-100')
            this.$refs.input.$el.style.height = this.quillHeight + 'px'
            setTimeout(() => {
              this.setEdjsFocus()
              this.$refs.input.$el.style.height = ''
            }, 200)
          }, 0)
        }

        this.$emit('focus')
      },
      blur() {
        if (this.data && this.data.blocks.length === 0 && this.focused && this.label && !this.menuOpened) {
          this.isBluring = true
          this.focused = false
          this.$refs.input.$el.style.height = this.$refs.input.$el.clientHeight + 'px'
          this.quillHeight = this.$refs.input.$el.clientHeight
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['p-2'], ['px-2', 'py-4.5'])
            this.$refs.label.classList.replace(
              ['text-12', 'leading-4', 'text-gray-600', 'top-2', 'font-semibold'],
              ['text-16', 'leading-5', 'text-gray-500', 'top-4.5', 'font-normal']
            )
            this.$refs.input.$el.classList.replace('opacity-100', 'opacity-0')
            this.$refs.input.$el.style.height = '0px'
            setTimeout(() => {
              this.$refs.input.$el.classList.replace('flex', 'hidden')
              this.$refs.input.$el.style.height = ''
              this.isBluring = false
            }, 200)
          }, 0)
        }
        this.$emit('blur')
      },
      setEdjsFocus() {
        const editor = this.$refs.input._data.state.editor

        editor.isReady
          .then(() => {
            const lastBlockIndex = editor.blocks.getBlocksCount() - 1
            if (lastBlockIndex >= 0) {
              editor.caret.setToBlock(lastBlockIndex, 'end')
            } else {
              editor.caret.setToBlock(0, 'start')
            }
          })
          .catch(reason => {
            console.log(`Editor.js initialization failed because of ${reason}`)
          })
      },
      getValue() {
        return this.data
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    watch: {
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    computed: {
      ...mapGetters(['theme'])
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
